import { createApi } from '@reduxjs/toolkit/query/react';

import { psychologistProfileBaseUrl } from '../configs/api/api.config';

import { createPsychologistServiceBaseQuery } from './psychologistService.baseQuery';

export interface PsychologistNotificationChannels {
  push: boolean;
  email: boolean;
}

export interface PsychologistNotificationSettings {
  meeting_info: PsychologistNotificationChannels;
  marketing: PsychologistNotificationChannels;
}

export interface PsychologistProfile {
  keycloak_sub: string;
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  phone_number: string | null;
  full_name: string | null;
  /** Presigned URL приватного S3-объекта, живёт 15 минут. Готовая ссылка — не склеивать с baseUrl. */
  avatar_url: string | null;
  birthday: string | null;
  time_zone: string | null;
  telegram: string | null;
  vk: string | null;
  note: string | null;
  specialization: string | null;
  notification_settings: PsychologistNotificationSettings;
}

/** PATCH принимает только эти поля: id, email, keycloak_sub, full_name, avatar_url и role запрещены. */
export type PsychologistProfilePatch = Partial<
  Pick<
    PsychologistProfile,
    | 'first_name'
    | 'last_name'
    | 'middle_name'
    | 'phone_number'
    | 'birthday'
    | 'time_zone'
    | 'telegram'
    | 'vk'
    | 'note'
    | 'specialization'
  > & { notification_settings: Partial<PsychologistNotificationSettings> }
>;

export interface PsychologistAvatarUploadResponse {
  avatar_url: string;
  profile: PsychologistProfile;
}

export const psychologistProfileApi = createApi({
  reducerPath: 'psychologistProfileApi',
  baseQuery: createPsychologistServiceBaseQuery(psychologistProfileBaseUrl),
  tagTypes: ['PsychologistProfile'],
  endpoints: (build) => ({
    getPsychologistProfile: build.query<PsychologistProfile, void>({
      query: () => '/psychologists/me/profile',
      providesTags: ['PsychologistProfile'],
    }),

    updatePsychologistProfile: build.mutation<PsychologistProfile, PsychologistProfilePatch>({
      query: (patch) => ({
        url: '/psychologists/me/profile',
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
      invalidatesTags: (result, error) => (error ? [] : ['PsychologistProfile']),
    }),

    uploadPsychologistAvatar: build.mutation<PsychologistAvatarUploadResponse, FormData>({
      query: (formData) => ({
        url: '/psychologists/me/profile/avatar',
        method: 'POST',
        body: formData,
      }),
      // Ответ уже содержит актуальный профиль — кладём его в кэш вместо повторного запроса.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            psychologistProfileApi.util.updateQueryData('getPsychologistProfile', undefined, () => data.profile),
          );
        } catch {
          // Ошибку загрузки показывает вызывающий компонент, здесь только обновление кэша.
        }
      },
    }),
  }),
});

export const {
  useGetPsychologistProfileQuery,
  useUpdatePsychologistProfileMutation,
  useUploadPsychologistAvatarMutation,
} = psychologistProfileApi;
